import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createBatchEventProcessor,
  createInstance,
  createPollingProjectConfigManager,
  OptimizelyUserContext,
} from '@optimizely/optimizely-sdk';
import { v4 as uuidv4 } from 'uuid';
import { OPTIMIZELY_DATAFILE as SDK_KEY } from 'config';
import getIsVipUser from 'lib/getIsVipUser';
import getUserGroup from 'lib/getUserGroup';
import getOptimizelyUserId from 'lib/getOptimizelyUserId';
import { registerOptimizelyExperimentTracker } from './registerOptimizelyExperimentTracker';
import { DecisionsType, OptimizelyContextValue, OptimizelyClientType } from './optimizely.types';
import { ALL_FLAG_KEYS } from './constants';

export const OptimizelyContext = createContext<OptimizelyContextValue>({
  decisions: null,
  isReady: false,
  userContext: null,
});

let optimizely: OptimizelyClientType | null = null;

// for testing purposes only
export const resetOptimizelyInstance = () => {
  optimizely = null;
};

export const getOptimizelyInstance = (): OptimizelyClientType => {
  if (!optimizely) {
    if (!SDK_KEY) {
      // eslint-disable-next-line no-console
      console.error('Optimizely SDK_KEY is missing. Optimizely will not initialize.');
    }

    const pollingConfigManager = createPollingProjectConfigManager({
      sdkKey: SDK_KEY ?? '',
      updateInterval: 60000,
    });

    const batchEventProcessor = createBatchEventProcessor({
      batchSize: 100,
      flushInterval: 3000,
    });

    optimizely = createInstance({
      projectConfigManager: pollingConfigManager,
      eventProcessor: batchEventProcessor,
    });
  }
  return optimizely as OptimizelyClientType;
};

export const makeDecisionsForFlags = (user: OptimizelyUserContext): DecisionsType => {
  return ALL_FLAG_KEYS.reduce((acc, flagKey) => {
    acc[flagKey] = user.decide(flagKey);
    return acc;
  }, {} as DecisionsType);
};

export const OptimizelyProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const [decisions, setDecisions] = useState<DecisionsType | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [userContext, setUserContext] = useState<OptimizelyUserContext | null>(null);

  const optimizelyUserId = getOptimizelyUserId();
  const ghUserGroup = getUserGroup();
  const isVipUser = getIsVipUser();
  const optimizelyInstance = getOptimizelyInstance();

  useEffect(() => {
    let isMounted = true; // Flag to prevent setting state on unmounted component

    // 1. Create the user context based on current attributes
    const user = optimizelyInstance.createUserContext(optimizelyUserId || uuidv4(), {
      user_group: ghUserGroup,
      is_vip: isVipUser,
    });

    // 2. Wait for the datafile to be ready and handle errors
    optimizelyInstance
      .onReady()
      .then(() => {
        if (!isMounted) return;

        const experimentFlags =
          makeDecisionsForFlags(user)?.['qantas-hotels-optimizely-feature-event']?.variables?.experiment_flags.experimentFlags || [];

        // The experiment tracker must be registered before decisions are made
        registerOptimizelyExperimentTracker({
          optimizelyInstance,
          experimentFlags,
          userContext: user,
          dispatch,
        });

        // Perform decisions only after ready
        const allDecisions = makeDecisionsForFlags(user);
        setDecisions(allDecisions);
        setIsReady(true);
        setUserContext(user);
      })
      .catch((error) => {
        if (!isMounted) return;

        // eslint-disable-next-line no-console
        console.error('Optimizely SDK failed to initialize or fetch datafile:', error);

        setDecisions(null);
        setIsReady(true);
        setUserContext(null);
      });

    // 3. Clean-up function
    return () => {
      isMounted = false;
    };
  }, [ghUserGroup, isVipUser, optimizelyUserId, optimizelyInstance, dispatch]);

  const contextValue = {
    decisions,
    isReady,
    userContext,
  };

  return <OptimizelyContext.Provider value={contextValue}>{children}</OptimizelyContext.Provider>;
};

OptimizelyProvider.displayName = 'OptimizelyProvider';
